import { authService } from "../auth/authService";
import { env } from "../config/env";

type HttpMethod = "GET" | "POST";

interface ApiRequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
}

// Deduplicates concurrent identical requests so only one fetch fires.
// Key: "METHOD:url:serialized-body"
const pendingRequests = new Map<string, Promise<any>>();

function requestKey(method: string, url: string, body?: unknown): string {
  return `${method}:${url}:${body !== undefined ? JSON.stringify(body) : ""}`;
}

export function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  return apiRequestBase<T>(env.posBaseUrl, path, options);
}

export function apiRequestBase<T>(
  baseUrl: string,
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const method = options.method ?? "GET";
  const base = baseUrl.replace(/\/+$/, "");
  const endpoint = path.startsWith("/") ? path : `/${path}`;
  const url = `${base}${endpoint}`;
  const key = requestKey(method, url, options.body);

  // Return the in-flight promise for an identical concurrent request.
  const existing = pendingRequests.get(key);
  if (existing) return existing as Promise<T>;

  const promise: Promise<T> = (async () => {
    const token = await authService.getAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers ?? {}),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const profileId = localStorage.getItem("profileId");
    const userId = localStorage.getItem("userId");
    if (profileId) headers.clientbusinessprofileid = profileId;
    if (userId) headers.userid = userId;
    headers.clientid = "0";
    headers.processcall = "true";

    const response = await fetch(url, {
      method,
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Request failed: ${response.status}`);
    }

    return (await response.json()) as T;
  })();

  pendingRequests.set(key, promise);
  promise.finally(() => pendingRequests.delete(key));

  return promise;
}

export function apiGet<T>(path: string) {
  return apiRequest<T>(path, { method: "GET" });
}

export function apiPost<T>(path: string, body?: unknown) {
  return apiRequest<T>(path, { method: "POST", body });
}

export function unwrapResponse<T>(response: any): T {
  if (response?.data !== undefined) return response.data as T;
  if (response?.Data !== undefined) return response.Data as T;
  if (response?.result !== undefined) return response.result as T;
  return response as T;
}
